// Sunice Product Card JavaScript
// Handles custom variant selection and interactions for product cards

// Guard: avoid redeclaration in Theme Editor when assets are re-injected
(function() {
  'use strict';
  
  const globalWindow = /** @type {Record<string, any>} */ (window);

// Make functions globally available for theme editor compatibility
/**
 * @param {HTMLElement} button
 */
globalWindow.toggleProductForm = function(button) {
  if (!(button instanceof HTMLElement)) return;

  const productCard = button.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  // On mobile (< 750px), open a full-screen modal instead of inline popover
  if (window.innerWidth < 750) {
    openMobileProductFormModal(productCard);
    return;
  }

  const formContainer = productCard.querySelector('.product-form-container');
  const selectButton = productCard.querySelector('.select-size-button');
  const closeButton = productCard.querySelector('.close-form-button');

  // Add active class to show form (desktop popover behavior)
  productCard.classList.add('active');

  // Show close button, hide select button
  if (selectButton instanceof HTMLElement) selectButton.style.display = 'none';
  if (closeButton instanceof HTMLElement) closeButton.style.display = 'inline-block';

  // Ensure initial variant selection when popover opens
  const picker = productCard.querySelector('.sunice-variant-picker');
  if (picker instanceof HTMLElement) {
    // Check if any options are already selected
    const hasSelection = picker.querySelector('input[type="radio"]:checked');
    if (!hasSelection) {
      // No selection yet, initialize with first color and size
      requestAnimationFrame(() => {
        setInitialVariantSelection(picker);
      });
    }
  }
};

/**
 * @param {HTMLElement} button
 */
globalWindow.closeProductForm = function(button) {
  if (!(button instanceof HTMLElement)) return;

  const productCard = button.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  // On mobile, close the full-screen modal instead of toggling inline popover
  if (window.innerWidth < 750) {
    try {
      /** @type {any} */
      const globalWindow = window;
      if (typeof globalWindow.closeSuniceProductFormModal === 'function') {
        globalWindow.closeSuniceProductFormModal();
      }
    } catch (e) {
      // Failed to close product form modal from close button
    }
    return;
  }

  const formContainer = productCard.querySelector('.product-form-container');
  const selectButton = productCard.querySelector('.select-size-button');
  const closeButton = productCard.querySelector('.close-form-button');

  // Remove active class to hide form
  productCard.classList.remove('active');

  // Show select button, hide close button
  if (selectButton instanceof HTMLElement) selectButton.style.display = 'inline-block';
  if (closeButton instanceof HTMLElement) closeButton.style.display = 'none';
};

/**
 * Open product form in a modal on mobile (< 750px).
 * Reuses the same form markup/styles, but shows it in a full-screen bottom sheet.
 * @param {HTMLElement} productCard
 */
function openMobileProductFormModal(productCard) {
  // Only for small screens
  if (window.innerWidth >= 750) return;

  /** @type {any} */
  const globalWindow = window;

  let modalEl = document.querySelector('.sunice-product-form-modal');
  if (!(modalEl instanceof HTMLElement)) {
    modalEl = document.createElement('div');
    modalEl.className = 'sunice-product-form-modal';
    modalEl.innerHTML = `
      <div class="sunice-product-form-modal__backdrop"></div>
      <div class="sunice-product-form-modal__dialog" role="dialog" aria-modal="true" aria-label="Select options">
        <button type="button" class="sunice-product-form-modal__close" aria-label="Close"></button>
        <div class="sunice-product-form-modal__content"></div>
      </div>
    `;
    document.body.appendChild(modalEl);

    const closeModal = () => {
      modalEl.classList.remove('is-open');
      modalEl.removeAttribute('data-product-id');
      const contentEl = modalEl.querySelector('.sunice-product-form-modal__content');
      if (contentEl instanceof HTMLElement) {
        contentEl.innerHTML = '';
      }
      document.documentElement.classList.remove('sunice-product-form-modal-open');
    };

    const backdrop = modalEl.querySelector('.sunice-product-form-modal__backdrop');
    const closeBtn = modalEl.querySelector('.sunice-product-form-modal__close');
    if (backdrop instanceof HTMLElement) {
      backdrop.addEventListener('click', closeModal);
    }
    if (closeBtn instanceof HTMLElement) {
      closeBtn.addEventListener('click', closeModal);
    }

    // Expose close function globally if needed elsewhere
    globalWindow.closeSuniceProductFormModal = closeModal;
  }

  const modal = modalEl; // For clearer semantics below
  const content = modalEl.querySelector('.sunice-product-form-modal__content');
  if (!(content instanceof HTMLElement)) return;

  const formContainer = productCard.querySelector('.product-form-container');
  if (!(formContainer instanceof HTMLElement)) return;

  // Clone the form container so desktop inline version is untouched
  const cloned = formContainer.cloneNode(true);

  // Reset picker initialization flags inside the cloned content
  const pickerNodes = cloned.querySelectorAll('.sunice-variant-picker');
  pickerNodes.forEach((node) => {
    if (node instanceof HTMLElement) {
      node.removeAttribute('data-initialized');
    }
  });

  // Remove the selection buttons (Select size / Close) from the modal copy
  const selectionButtons = cloned.querySelector('.sunice-product-card__selection-buttons');
  if (selectionButtons instanceof HTMLElement) {
    selectionButtons.style.display = 'none';
  }

  content.innerHTML = '';
  content.appendChild(cloned);

  // Re-initialize variant picker behavior for the modal content only
  // First, remove data-initialized from the cloned picker so it can be initialized
  const clonedPicker = cloned.querySelector('.sunice-variant-picker');
  if (clonedPicker instanceof HTMLElement) {
    clonedPicker.removeAttribute('data-initialized');
  }
  
  if (typeof globalWindow.initializeSuniceProductCardComponents === 'function') {
    globalWindow.initializeSuniceProductCardComponents();
  } else if (typeof globalWindow.setupSuniceVariantPicker === 'function') {
    globalWindow.setupSuniceVariantPicker();
  }

  // Ensure initial variant selection when modal opens
  if (clonedPicker instanceof HTMLElement) {
    // Wait for initialization to complete, then set initial selections
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setInitialVariantSelection(clonedPicker);
      });
    });
  }

  // Re-initialize variant title handlers for the cloned form in the modal
  // This ensures color swatch clicks in the modal update the variant-title in the original product card
  try {
    // Find color swiper containers in the cloned form
    const clonedColorSwipers = cloned.querySelectorAll('.color-swiper-container');
    clonedColorSwipers.forEach((swiperContainer) => {
      if (swiperContainer instanceof HTMLElement) {
        // Set up variant title updates, but target the original product card
        const colorSwatches = swiperContainer.querySelectorAll('.color-swatch');
        if (colorSwatches.length > 0) {
          const variantTitle = productCard.querySelector('.variant-title');
          if (variantTitle) {
            // Set initial variant title
            const activeSwatch = Array.from(colorSwatches).find(s => 
              s.getAttribute('data-active') === 'true'
            ) || Array.from(colorSwatches).find(s => 
              s.getAttribute('data-option-available') === 'true'
            );
            
            if (activeSwatch) {
              const colorName = activeSwatch.getAttribute('data-color') || 
                               activeSwatch.getAttribute('title') || '';
              if (colorName) {
                variantTitle.textContent = colorName;
              }
            }
            
            // Add click handlers to update variant selection AND variant-title
            colorSwatches.forEach((swatch) => {
              const handleColorSwatchClick = function(event) {
                if (event) {
                  event.preventDefault();
                  event.stopPropagation();
                }
                
                // Get color name from the radio button's value inside this swatch, or from data attributes
                const radio = this.querySelector('input[type="radio"]');
                const colorName = radio instanceof HTMLInputElement ? radio.value :
                                 this.getAttribute('data-color') || 
                                 this.getAttribute('title') || '';
                
                if (!colorName) {
                  return;
                }
                
                // Update variant-title in original product card
                if (colorName && variantTitle) {
                  variantTitle.textContent = colorName;
                }
                
                // Update active state on all swatches (both in modal and original card)
                const allSwatches = productCard.querySelectorAll('.color-swatch');
                allSwatches.forEach((s) => {
                  s.removeAttribute('data-active');
                });
                // Also update in modal
                cloned.querySelectorAll('.color-swatch').forEach((s) => {
                  s.removeAttribute('data-active');
                });
                this.setAttribute('data-active', 'true');
                
                // Find and check the corresponding radio button in the modal's variant picker
                const modalPicker = cloned.querySelector('.sunice-variant-picker');
                if (modalPicker instanceof HTMLElement) {
                  const colorFieldsets = Array.from(modalPicker.querySelectorAll('fieldset'));
                  for (const fieldset of colorFieldsets) {
                    if (!(fieldset instanceof HTMLFieldSetElement)) continue;
                    const legend = fieldset.querySelector('legend');
                    const optionName = legend?.textContent?.trim();
                    if (optionName && (optionName.toLowerCase() === 'color' || optionName.toLowerCase() === 'colour')) {
                      // Find the radio button with matching value
                      const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
                      for (const radio of radioButtons) {
                        if (!(radio instanceof HTMLInputElement)) continue;
                        const normalizedRadioValue = radio.value.toLowerCase().trim();
                        const normalizedColorValue = colorName.toLowerCase().trim();
                        if (normalizedRadioValue === normalizedColorValue) {
                          // Uncheck all other radios in this fieldset first
                          radioButtons.forEach((r) => {
                            if (r instanceof HTMLInputElement && r !== radio) {
                              r.checked = false;
                              const rLabel = r.closest('label');
                              if (rLabel instanceof HTMLElement) {
                                rLabel.classList.remove('active');
                                rLabel.removeAttribute('data-active');
                              }
                            }
                          });
                          
                          // Get the label for this radio
                          const radioLabel = radio.closest('label.sunice-variant-option__button-label');
                          if (radioLabel instanceof HTMLElement) {
                            // Uncheck all other radios in this fieldset
                            radioButtons.forEach((r) => {
                              if (r instanceof HTMLInputElement && r !== radio) {
                                r.checked = false;
                                const rLabel = r.closest('label');
                                if (rLabel instanceof HTMLElement) {
                                  rLabel.classList.remove('active');
                                  rLabel.removeAttribute('data-active');
                                }
                              }
                            });
                            
                            // Check this radio
                            radio.checked = true;
                            
                            // Update active class
                            const fieldset = radioLabel.closest('fieldset');
                            if (fieldset instanceof HTMLFieldSetElement) {
                              const allLabels = fieldset.querySelectorAll('label');
                              allLabels.forEach(l => {
                                l.classList.remove('active');
                                l.removeAttribute('data-active');
                              });
                            }
                            radioLabel.classList.add('active');
                            if (radioLabel.classList.contains('color-swatch')) {
                              radioLabel.setAttribute('data-active', 'true');
                            }
                            
                            // Call handleVariantChange to update variant ID
                            handleVariantChange(radio, modalPicker);
                            
                            // Call updateAvailabilityForColorSelection in requestAnimationFrame (exact same as desktop)
                            requestAnimationFrame(() => {
                              updateAvailabilityForColorSelection(modalPicker, radio.value);
                            });
                          }
                          break;
                        }
                      }
                    }
                  }
                }
              };
              
              // Remove any existing listeners to avoid duplicates
              const newSwatch = swatch.cloneNode(true);
              swatch.parentNode?.replaceChild(newSwatch, swatch);
              
              // Add both click and touch listeners for mobile support
              newSwatch.addEventListener('click', handleColorSwatchClick);
              newSwatch.addEventListener('touchend', function(e) {
                e.preventDefault();
                handleColorSwatchClick.call(this, e);
              }, { passive: false });
            });
          }
        }
      }
    });
    
    // Also handle non-swiper color swatches in the modal
    const clonedColorSwatches = cloned.querySelectorAll('.color-swatch:not(.color-swiper-container .color-swatch)');
    if (clonedColorSwatches.length > 0) {
      const variantTitle = productCard.querySelector('.variant-title');
      if (variantTitle) {
        clonedColorSwatches.forEach((swatch) => {
          const handleColorSwatchClick = function(event) {
            if (event) {
              event.preventDefault();
              event.stopPropagation();
            }
            
            // Get color name from the radio button's value inside this label
            const radio = this.querySelector('input[type="radio"]');
            const colorName = radio instanceof HTMLInputElement ? radio.value : 
                             this.getAttribute('data-color') || 
                             this.getAttribute('title') || '';
            
            if (!colorName) {
              return;
            }
            
            // Update variant-title in original product card
            if (colorName && variantTitle) {
              variantTitle.textContent = colorName;
            }
            
            // Update active state
            productCard.querySelectorAll('.color-swatch').forEach((s) => {
              s.removeAttribute('data-active');
            });
            cloned.querySelectorAll('.color-swatch').forEach((s) => {
              s.removeAttribute('data-active');
            });
            this.setAttribute('data-active', 'true');
            
            // Find and check the corresponding radio button in the modal's variant picker
            const modalPicker = cloned.querySelector('.sunice-variant-picker');
            if (modalPicker instanceof HTMLElement && radio instanceof HTMLInputElement) {
              // Find the fieldset that contains this radio button (it's the color fieldset)
              const colorFieldset = radio.closest('fieldset');
              
              if (colorFieldset instanceof HTMLFieldSetElement) {
                // Use the radio button we found in the clicked label
                const radioButtons = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
                
                // Uncheck all other radios in this fieldset first
                radioButtons.forEach((r) => {
                  if (r instanceof HTMLInputElement && r !== radio) {
                    r.checked = false;
                    const rLabel = r.closest('label');
                    if (rLabel instanceof HTMLElement) {
                      rLabel.classList.remove('active');
                      rLabel.removeAttribute('data-active');
                    }
                  }
                });
                
                // Check this radio
                radio.checked = true;
                
                // Update active class
                const radioLabel = radio.closest('label.sunice-variant-option__button-label');
                if (radioLabel instanceof HTMLElement) {
                  const fieldsetEl = radioLabel.closest('fieldset');
                  if (fieldsetEl instanceof HTMLFieldSetElement) {
                    const allLabels = fieldsetEl.querySelectorAll('label');
                    allLabels.forEach(l => {
                      l.classList.remove('active');
                      l.removeAttribute('data-active');
                    });
                  }
                  radioLabel.classList.add('active');
                  if (radioLabel.classList.contains('color-swatch')) {
                    radioLabel.setAttribute('data-active', 'true');
                  }
                  
                  // Call handleVariantChange to update variant ID
                  handleVariantChange(radio, modalPicker);
                  
                  // Call updateAvailabilityForColorSelection in requestAnimationFrame (exact same as desktop)
                  requestAnimationFrame(() => {
                    updateAvailabilityForColorSelection(modalPicker, radio.value);
                  });
                }
              }
            }
          };
          
          const newSwatch = swatch.cloneNode(true);
          swatch.parentNode?.replaceChild(newSwatch, swatch);
          
          newSwatch.addEventListener('click', handleColorSwatchClick);
          newSwatch.addEventListener('touchend', function(e) {
            e.preventDefault();
            handleColorSwatchClick.call(this, e);
          }, { passive: false });
        });
      }
    }
  } catch (e) {
    // Failed to initialize variant title handlers in modal
  }

  modal.setAttribute('data-product-id', productCard.dataset.productId || '');
  modal.classList.add('is-open');
  document.documentElement.classList.add('sunice-product-form-modal-open');
}

// Custom Sunice Variant Picker Handler - Theme Editor Compatible
function setupSuniceVariantPicker() {
  const suniceVariantPickers = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll('.sunice-variant-picker:not([data-initialized])')
  );

  suniceVariantPickers.forEach((picker, index) => {
    // Mark as initialized to prevent duplicate event listeners
    picker.setAttribute('data-initialized', 'true');

    // Handle variant option changes
    picker.addEventListener('change', function(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type === 'radio') {
        handleVariantChange(target, picker);
      }
    });

    // Handle click events on labels to ensure change event fires
    // (Labels automatically check radio buttons, but we ensure the change event is handled)
    picker.addEventListener('click', function(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      
      // Handle clicks on labels (which contain the radio buttons)
      const label = target.closest('label.sunice-variant-option__button-label');
      if (label instanceof HTMLElement) {
        const radio = label.querySelector('input[type="radio"]');
        if (radio instanceof HTMLInputElement && !radio.disabled) {
          // Get the fieldset to uncheck other radios in the same group
          const fieldset = label.closest('fieldset');
          if (fieldset instanceof HTMLFieldSetElement) {
            const allRadiosInGroup = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
            allRadiosInGroup.forEach((r) => {
              if (r instanceof HTMLInputElement && r !== radio) {
                r.checked = false;
              }
            });
          }
          
          // Ensure radio is checked (label click should do this, but be explicit)
          radio.checked = true;
          
          // IMMEDIATELY update active class like variant-picker.js does
          if (fieldset instanceof HTMLFieldSetElement) {
            // Remove active class from all labels in the same fieldset
            const allLabels = fieldset.querySelectorAll('label');
            allLabels.forEach(l => {
              l.classList.remove('active');
              l.removeAttribute('data-active');
            });
          }
          
          // Add active class to the clicked label
          label.classList.add('active');
          
          // For color swatches, also set data-active attribute
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
          
          // Check if this is a color swatch click
          const isColorSwatch = label.classList.contains('color-swatch');
          
          // Get the option name to determine what was clicked
          const productId = picker.dataset.productId || '';
          const optionName = radio.name.replace(`-${productId}`, '');
          const isColorOption = optionName === 'Color' || optionName === 'Colour';
          
          // Call handleVariantChange to update other state
          handleVariantChange(radio, picker);
          
          // Then update availability for other options based on selection
          // Use requestAnimationFrame to ensure DOM is fully updated
          requestAnimationFrame(() => {
            if (isColorSwatch || isColorOption) {
              updateAvailabilityForColorSelection(picker, radio.value);
            } else {
              // Size or other option clicked, update color availability
              updateAvailabilityForSizeSelection(picker, optionName, radio.value);
            }
          });
        }
      }
    });

    // Set initial variant selection - select the first available complete variant
    setInitialVariantSelection(picker);
    
    // Also calculate unavailable states on initialization
    updateUnavailableStates(picker);
  });
}

// Set initial variant selection by selecting the first available color and size
/**
 * @param {HTMLElement} picker
 */
function setInitialVariantSelection(picker) {
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) return;

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);

  // Find Color/Colour and Size options
  const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
  const sizeOption = optionsList.find(opt => opt?.name === 'Size');
  
  if (!colorOption || !sizeOption) {
    // Fallback to old behavior if Color/Size options not found
    const firstVariant = variantsList.find((variant) => variant?.available) || variantsList[0];
    if (!firstVariant) return;
    selectVariantOptions(picker, firstVariant, optionsList, variantsList);
    return;
  }

  const colorOptionPosition = colorOption.position - 1;
  const sizeOptionPosition = sizeOption.position - 1;

  // Find first available color (color that has at least one available variant)
  let firstAvailableColor = null;
  const colorFieldset = picker.querySelector(`fieldset[data-option-id="option-${colorOption.position}"]`) ||
                       Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                         const legend = fs.querySelector('legend');
                         return legend && (legend.textContent.trim() === 'Color' || legend.textContent.trim() === 'Colour' || legend.textContent.trim().toLowerCase().includes('color'));
                       });
  
  if (colorFieldset instanceof HTMLFieldSetElement) {
    const colorRadios = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
    for (const radio of colorRadios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const colorValue = radio.value;
      // Check if this color has at least one available variant
      const hasAvailableVariant = variantsList.some(variant => {
        const variantColor = variant.options?.[colorOptionPosition];
        return variantColor && variantColor.toString().toLowerCase().trim() === colorValue.toString().toLowerCase().trim() && variant.available;
      });
      if (hasAvailableVariant) {
        firstAvailableColor = colorValue;
        break;
      }
    }
    // If no available color found, use first color
    if (!firstAvailableColor && colorRadios.length > 0) {
      const firstRadio = colorRadios[0];
      if (firstRadio instanceof HTMLInputElement) {
        firstAvailableColor = firstRadio.value;
      }
    }
  }

  // Find first available size (size that has at least one available variant)
  let firstAvailableSize = null;
  const sizeFieldset = picker.querySelector(`fieldset[data-option-id="option-${sizeOption.position}"]`) ||
                      Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                        const legend = fs.querySelector('legend');
                        return legend && legend.textContent.trim() === 'Size';
                      });
  
  if (sizeFieldset instanceof HTMLFieldSetElement) {
    const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    for (const radio of sizeRadios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const sizeValue = radio.value;
      // Check if this size has at least one available variant
      const hasAvailableVariant = variantsList.some(variant => {
        const variantSize = variant.options?.[sizeOptionPosition];
        return variantSize && variantSize.toString().toLowerCase().trim() === sizeValue.toString().toLowerCase().trim() && variant.available;
      });
      if (hasAvailableVariant) {
        firstAvailableSize = sizeValue;
        break;
      }
    }
    // If no available size found, use first size
    if (!firstAvailableSize && sizeRadios.length > 0) {
      const firstRadio = sizeRadios[0];
      if (firstRadio instanceof HTMLInputElement) {
        firstAvailableSize = firstRadio.value;
      }
    }
  }

  // Select the first available color
  if (firstAvailableColor && colorFieldset instanceof HTMLFieldSetElement) {
    const colorRadios = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
    colorRadios.forEach(radio => {
      if (!(radio instanceof HTMLInputElement)) return;
      if (radio.value.toString().toLowerCase().trim() === firstAvailableColor.toString().toLowerCase().trim()) {
        radio.checked = true;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.add('active');
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
        }
      } else {
        radio.checked = false;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.remove('active');
          label.removeAttribute('data-active');
        }
      }
    });
  }

  // Select the first available size
  if (firstAvailableSize && sizeFieldset instanceof HTMLFieldSetElement) {
    const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    sizeRadios.forEach(radio => {
      if (!(radio instanceof HTMLInputElement)) return;
      if (radio.value.toString().toLowerCase().trim() === firstAvailableSize.toString().toLowerCase().trim()) {
        radio.checked = true;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.add('active');
        }
      } else {
        radio.checked = false;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.remove('active');
        }
      }
    });
  }

  // Find variant that matches the selected color and size
  let matchingVariant = null;
  if (firstAvailableColor && firstAvailableSize) {
    matchingVariant = variantsList.find(variant => {
      const variantColor = variant.options?.[colorOptionPosition];
      const variantSize = variant.options?.[sizeOptionPosition];
      const colorMatch = variantColor && variantColor.toString().toLowerCase().trim() === firstAvailableColor.toString().toLowerCase().trim();
      const sizeMatch = variantSize && variantSize.toString().toLowerCase().trim() === firstAvailableSize.toString().toLowerCase().trim();
      return colorMatch && sizeMatch;
    });
  }

  // If no exact match, try to find first available variant with the selected color
  if (!matchingVariant && firstAvailableColor) {
    matchingVariant = variantsList.find(variant => {
      const variantColor = variant.options?.[colorOptionPosition];
      const colorMatch = variantColor && variantColor.toString().toLowerCase().trim() === firstAvailableColor.toString().toLowerCase().trim();
      return colorMatch && variant.available;
    });
  }

  // Final fallback: first available variant
  if (!matchingVariant) {
    matchingVariant = variantsList.find((variant) => variant?.available) || variantsList[0];
  }

  if (!matchingVariant) return;

  // Update the hidden variant ID input
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  const variantIdInput = form?.querySelector('input[name="id"]');
  if (variantIdInput instanceof HTMLInputElement) {
    variantIdInput.value = String(matchingVariant.id);
  }

  // Dispatch events and update states
  const initialSelectedOptions = /** @type {Record<string, string>} */ ({});
  optionsList.forEach((option) => {
    if (!option || typeof option.position !== 'number') return;
    const position = option.position - 1;
    if (position < 0) return;
    const optionValue = matchingVariant.options?.[position];
    if (optionValue == null) return;
    initialSelectedOptions[option.name] = optionValue;
  });

  dispatchVariantEvents(picker, matchingVariant, initialSelectedOptions);
  updateActiveStates(picker);

  // Update availability based on selected color
  if (firstAvailableColor) {
    requestAnimationFrame(() => {
      updateAvailabilityForColorSelection(picker, firstAvailableColor);
    });
  }

  updateProductCardSwatchBackground(picker, initialSelectedOptions);

  const productCard = picker.closest('.sunice-product-card');
  if (productCard instanceof HTMLElement) {
    const activeInput = picker.querySelector('input[type="radio"][data-image-url][checked]');
    const { primary: initialPrimaryImageUrl, secondary: initialSecondaryImageUrl } = getActiveVariantImageUrls(
      picker,
      productCard,
      activeInput instanceof HTMLInputElement ? activeInput : null
    );
    updateProductCardPrimaryImage(productCard, initialPrimaryImageUrl, initialSecondaryImageUrl);
  }
}

// Helper function to select variant options (fallback for non-Color/Size products)
/**
 * @param {HTMLElement} picker
 * @param {any} variant
 * @param {Array<any>} optionsList
 * @param {Array<any>} variantsList
 */
function selectVariantOptions(picker, variant, optionsList, variantsList) {
  const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
  fieldsets.forEach((fieldset) => {
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const legend = fieldset.querySelector('legend');
    const optionName = legend?.textContent?.trim();
    if (!optionName) return;

    const option = optionsList.find((opt) => opt?.name === optionName);

    if (option) {
      const optionPosition = option.position - 1; // Convert to 0-based index
      const variantValue = variant.options?.[optionPosition];

      // Find and select the radio button with this value
      const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      radioButtons.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        if (radio.value === variantValue) {
          radio.checked = true;
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.add('active');
            if (label.classList.contains('color-swatch')) {
              label.setAttribute('data-active', 'true');
            }
          }
        } else {
          radio.checked = false;
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.remove('active');
            label.removeAttribute('data-active');
          }
        }
      });
    }
  });

  // Update the hidden variant ID input
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  const variantIdInput = form?.querySelector('input[name="id"]');
  if (variantIdInput instanceof HTMLInputElement) {
    variantIdInput.value = String(variant.id);
  }

  // Dispatch events and update states
  const initialSelectedOptions = /** @type {Record<string, string>} */ ({});
  optionsList.forEach((option) => {
    if (!option || typeof option.position !== 'number') return;
    const position = option.position - 1;
    if (position < 0) return;
    const optionValue = variant.options?.[position];
    if (optionValue == null) return;
    initialSelectedOptions[option.name] = optionValue;
  });

  dispatchVariantEvents(picker, variant, initialSelectedOptions);
  updateActiveStates(picker);

  updateProductCardSwatchBackground(picker, initialSelectedOptions);

  const productCard = picker.closest('.sunice-product-card');
  if (productCard instanceof HTMLElement) {
    const activeInput = picker.querySelector('input[type="radio"][data-image-url][checked]');
    const { primary: initialPrimaryImageUrl, secondary: initialSecondaryImageUrl } = getActiveVariantImageUrls(
      picker,
      productCard,
      activeInput instanceof HTMLInputElement ? activeInput : null
    );
    updateProductCardPrimaryImage(productCard, initialPrimaryImageUrl, initialSecondaryImageUrl);
  }
}

// Handle variant change for custom picker
/**
 * @param {HTMLInputElement} selectedOption
 * @param {HTMLElement} picker
 */
function handleVariantChange(selectedOption, picker) {
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  const variantIdInput = form?.querySelector('input[name="id"]');

  // Get product variants data
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) return;

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);

  // Get all selected options for this product
  const selectedOptions = /** @type {Record<string, string>} */ ({});
  const radioButtons = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));

  radioButtons.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    // Get option name from radio name (e.g., "Size-123" -> "Size")
    // Use the product ID to properly split the name
    const productId = picker.dataset.productId || '';
    const optionName = radio.name.replace(`-${productId}`, '');
    const optionValue = radio.value;
    selectedOptions[optionName] = optionValue;
  });

  // Find variant that matches ALL selected options
  let matchingVariant = null;

  for (const variant of variantsList) {
    let matches = true;

    // Check if this variant matches all selected options
    for (const optionName in selectedOptions) {
      const option = optionsList.find((opt) => opt?.name === optionName);
      if (!option) continue;

      const optionPosition = option.position - 1; // Convert to 0-based index
      const expectedValue = selectedOptions[optionName];
      const variantValue = variant.options?.[optionPosition];

      // More flexible matching - handle case differences and whitespace
      if (
        variantValue &&
        expectedValue &&
        variantValue.toString().toLowerCase().trim() !== expectedValue.toString().toLowerCase().trim()
      ) {
        matches = false;
        break;
      }
    }

    if (matches) {
      matchingVariant = variant;
      break;
    }
  }

  if (matchingVariant && variantIdInput instanceof HTMLInputElement) {
    variantIdInput.value = String(matchingVariant.id);

    // Dispatch events
    dispatchVariantEvents(picker, matchingVariant, selectedOptions);
  } else {

    // Fallback: try to find any variant that matches at least one selected option
    let fallbackVariant = null;
    for (const variant of variantsList) {
      for (const optionName in selectedOptions) {
        const option = optionsList.find((opt) => opt?.name === optionName);
        if (!option) continue;

        const optionPosition = option.position - 1;
        const expectedValue = selectedOptions[optionName];
        const variantValue = variant.options?.[optionPosition];

        if (
          variantValue &&
          expectedValue &&
          variantValue.toString().toLowerCase().trim() === expectedValue.toString().toLowerCase().trim()
        ) {
          fallbackVariant = variant;
          break;
        }
      }
      if (fallbackVariant) break;
    }

    // If still no match, use the first variant
    if (!fallbackVariant && variantsList.length > 0) {
      fallbackVariant = variantsList[0];
    }

    if (fallbackVariant && variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = String(fallbackVariant.id);

      // Dispatch events for fallback variant
      dispatchVariantEvents(picker, fallbackVariant, selectedOptions);
    }
  }

  // Always update active and unavailable states, even if no variant matches
  // This ensures that when a color is selected, size availability updates correctly
  updateActiveStates(picker);

  updateProductCardSwatchBackground(picker, selectedOptions);

  const productCard = picker.closest('.sunice-product-card');
  if (productCard instanceof HTMLElement) {
    const { primary: primaryImageUrl, secondary: secondaryImageUrl } = getActiveVariantImageUrls(
      picker,
      productCard,
      selectedOption
    );
    updateProductCardPrimaryImage(productCard, primaryImageUrl, secondaryImageUrl);
  }
}

// Dispatch variant events for theme compatibility
/**
 * @param {HTMLElement} picker
 * @param {any} variant
 * @param {Record<string, string>} selectedOptions
 */
function dispatchVariantEvents(picker, variant, selectedOptions) {
  // Dispatch variant selected event for theme compatibility
  picker.dispatchEvent(new CustomEvent('variant-selected', {
    detail: {
      variantId: variant.id,
      variant: variant,
      selectedOptions: selectedOptions
    },
    bubbles: true
  }));

  // Also dispatch the theme's expected events
  const variantSelectedEvent = new CustomEvent('variant:selected', {
    detail: {
      id: variant.id,
      variant: variant
    },
    bubbles: true
  });
  picker.dispatchEvent(variantSelectedEvent);

  // Dispatch on the form as well for add-to-cart compatibility
  const form = picker.closest('form');
  if (form) {
    form.dispatchEvent(new CustomEvent('variant:selected', {
      detail: {
        id: variant.id,
        variant: variant
      },
      bubbles: true
    }));
  }

  // Also dispatch on the product card for broader compatibility
  const productCard = picker.closest('.sunice-product-card');
  if (productCard) {
    productCard.dispatchEvent(new CustomEvent('variant:selected', {
      detail: {
        id: variant.id,
        variant: variant
      },
      bubbles: true
    }));
  }
}

// Update active states for visual feedback
/**
 * @param {HTMLElement} picker
 */
function updateActiveStates(picker) {
  // Update active states for radio buttons
  const radioButtons = Array.from(picker.querySelectorAll('input[type="radio"]'));
  radioButtons.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) return;

    if (radio.checked) {
      label.classList.add('active');
      // Set data-active for color swatches
      if (label.classList.contains('color-swatch')) {
        label.setAttribute('data-active', 'true');
      }
    } else {
      label.classList.remove('active');
      // Remove data-active for color swatches
      if (label.classList.contains('color-swatch')) {
        label.removeAttribute('data-active');
      }
    }
  });

  // Update unavailable states based on current selections
  updateUnavailableStates(picker);
}

/**
 * Update availability for other options when a color is selected
 * @param {HTMLElement} picker - The variant picker element
 * @param {string} selectedColorValue - The selected color value
 */
function updateAvailabilityForColorSelection(picker, selectedColorValue) {
  // Get product variants data
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) {
    return;
  }

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);
  
  // Find the Color/Colour option to get its position
  const colorOption = optionsList.find(opt => 
    opt?.name === 'Color' || opt?.name === 'Colour'
  );
  if (!colorOption) {
    return;
  }
  
  const colorOptionPosition = colorOption.position - 1; // Convert to 0-based index
  
  // Normalize the selected color value for comparison
  const normalizeValue = (val) => {
    if (!val) return '';
    return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
  };
  const normalizedSelectedColor = normalizeValue(selectedColorValue);
  
  // Find all variants that have this color
  const variantsWithThisColor = variantsList.filter(variant => {
    const variantColorValue = variant.options?.[colorOptionPosition];
    const normalizedVariantColor = normalizeValue(variantColorValue);
    return normalizedVariantColor === normalizedSelectedColor;
  });
  
  // For each other option (not Color), update availability
  optionsList.forEach(option => {
    // Skip the Color option itself
    if (option.name === 'Color' || option.name === 'Colour') {
      return;
    }
    
    const optionPosition = option.position - 1; // Convert to 0-based index
    
    // Get all labels for this option
    // Try multiple selectors to find the fieldset
    let fieldset = picker.querySelector(`fieldset[data-option-id="option-${option.position}"]`);
    if (!fieldset) {
      // Fallback: try finding by legend text
      const allFieldsets = Array.from(picker.querySelectorAll('fieldset'));
      fieldset = allFieldsets.find(fs => {
        const legend = fs.querySelector('legend');
        return legend && legend.textContent.trim() === option.name;
      });
    }
    if (!fieldset) {
      return;
    }
    
    // Try to find labels - use multiple selectors for compatibility
    // First try the specific class
    let labels = Array.from(fieldset.querySelectorAll('label.sunice-variant-option__button-label'));
    
    // If none found, try all labels (fallback)
    if (labels.length === 0) {
      labels = Array.from(fieldset.querySelectorAll('label'));
    }
    
    // Filter out color swatch labels (we only want size/other option labels)
    labels = labels.filter(label => !label.classList.contains('color-swatch'));
    
    labels.forEach(label => {
      const radio = label.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) return;
      
      const optionValue = radio.value;
      const normalizedOptionValue = normalizeValue(optionValue);
      
      // Check if there's an available variant with this color + this option value
      const hasAvailableVariant = variantsWithThisColor.some(variant => {
        const variantOptionValue = variant.options?.[optionPosition];
        const normalizedVariantOption = normalizeValue(variantOptionValue);
        const matches = normalizedVariantOption === normalizedOptionValue;
        const isAvailable = variant.available;
        return matches && isAvailable;
      });
      
      // Also check if there's ANY variant (even unavailable) with this combination
      const hasAnyVariant = variantsWithThisColor.some(variant => {
        const variantOptionValue = variant.options?.[optionPosition];
        const normalizedVariantOption = normalizeValue(variantOptionValue);
        return normalizedVariantOption === normalizedOptionValue;
      });
      
      // Update the label's availability state (matching variant-picker.js pattern)
      // Always update availability, even if the radio is checked (to ensure visual consistency)
      if (hasAvailableVariant) {
        // Available: set data-option-available="true" on both label and input
        label.classList.remove('unavailable');
        label.removeAttribute('data-unavailable');
        label.setAttribute('data-option-available', 'true');
        radio.setAttribute('data-option-available', 'true');
        radio.disabled = false;
      } else if (hasAnyVariant) {
        // Variant exists but is unavailable - mark as unavailable
        // Only add unavailable class if not checked (to avoid visual issues with selected unavailable variants)
        if (!radio.checked) {
          label.classList.add('unavailable');
          label.setAttribute('data-unavailable', 'true');
        }
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
        radio.disabled = false;
      } else {
        // No variant exists with this combination - mark as unavailable
        // Only add unavailable class if not checked (to avoid visual issues with selected unavailable variants)
        if (!radio.checked) {
          label.classList.add('unavailable');
          label.setAttribute('data-unavailable', 'true');
        }
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
        radio.disabled = false;
      }
      
      // Force a style recalculation to ensure CSS updates
      void label.offsetHeight;
    });
  });
}

/**
 * Update availability for colors when a size (or other non-color option) is selected
 * @param {HTMLElement} picker - The variant picker element
 * @param {string} selectedOptionName - The selected option name (e.g., "Size")
 * @param {string} selectedOptionValue - The selected option value
 */
function updateAvailabilityForSizeSelection(picker, selectedOptionName, selectedOptionValue) {
  // Get product variants data
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) {
    return;
  }

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);
  
  // Find the selected option to get its position
  const selectedOption = optionsList.find(opt => opt?.name === selectedOptionName);
  if (!selectedOption) {
    return;
  }
  
  const selectedOptionPosition = selectedOption.position - 1; // Convert to 0-based index
  
  // Find the Color/Colour option to get its position
  const colorOption = optionsList.find(opt => 
    opt?.name === 'Color' || opt?.name === 'Colour'
  );
  if (!colorOption) {
    return;
  }
  
  const colorOptionPosition = colorOption.position - 1; // Convert to 0-based index
  
  // Normalize the selected option value for comparison
  const normalizeValue = (val) => {
    if (!val) return '';
    return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
  };
  const normalizedSelectedValue = normalizeValue(selectedOptionValue);
  
  // Find all variants that have this selected option value
  const variantsWithThisOption = variantsList.filter(variant => {
    const variantOptionValue = variant.options?.[selectedOptionPosition];
    const normalizedVariantOption = normalizeValue(variantOptionValue);
    return normalizedVariantOption === normalizedSelectedValue;
  });
  
  // Get the color fieldset and update color availability
  const colorFieldset = picker.querySelector(`fieldset[data-option-id="option-${colorOption.position}"]`);
  if (!colorFieldset) {
    return;
  }
  
  const colorLabels = Array.from(colorFieldset.querySelectorAll('label.sunice-variant-option__button-label'));
  
  colorLabels.forEach(label => {
    const radio = label.querySelector('input[type="radio"]');
    if (!(radio instanceof HTMLInputElement)) return;
    
    const colorValue = radio.value;
    const normalizedColorValue = normalizeValue(colorValue);
    
    // Check if there's an available variant with this color + this selected option value
    const hasAvailableVariant = variantsWithThisOption.some(variant => {
      const variantColorValue = variant.options?.[colorOptionPosition];
      const normalizedVariantColor = normalizeValue(variantColorValue);
      const matches = normalizedVariantColor === normalizedColorValue;
      const isAvailable = variant.available;
      return matches && isAvailable;
    });
    
    // Also check if there's ANY variant (even unavailable) with this combination
    const hasAnyVariant = variantsWithThisOption.some(variant => {
      const variantColorValue = variant.options?.[colorOptionPosition];
      const normalizedVariantColor = normalizeValue(variantColorValue);
      return normalizedVariantColor === normalizedColorValue;
    });
    
    // Update the label's availability state (matching variant-picker.js pattern)
    if (hasAvailableVariant) {
      // Available: set data-option-available="true" on both label and input
      label.classList.remove('unavailable');
      label.removeAttribute('data-unavailable');
      label.setAttribute('data-option-available', 'true');
      radio.setAttribute('data-option-available', 'true');
      radio.disabled = false;
    } else if (hasAnyVariant) {
      // Variant exists but is unavailable - still show it but mark as unavailable
      if (!radio.checked) {
        label.classList.add('unavailable');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      }
      radio.disabled = false;
    } else {
      // No variant exists with this combination
      if (!radio.checked) {
        label.classList.add('unavailable');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      }
      radio.disabled = false;
    }
  });
}

/**
 * Update unavailable states for variant options based on current selections
 * @param {HTMLElement} picker
 */
function updateUnavailableStates(picker) {
  // Get product variants data
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) {
    return;
  }

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);

  // Get currently selected options
  const selectedOptions = /** @type {Record<string, string>} */ ({});
  const checkedRadios = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));
  const productId = picker.dataset.productId || '';

  checkedRadios.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const optionName = radio.name.replace(`-${productId}`, '');
    selectedOptions[optionName] = radio.value;
  });

  // First, reset ALL unavailable states to ensure clean recalculation
  // We'll recalculate everything below, so we need to clear previous states
  const allLabels = Array.from(picker.querySelectorAll('label.sunice-variant-option__button-label'));
  allLabels.forEach((label) => {
    if (!(label instanceof HTMLElement)) return;
    const radio = label.querySelector('input[type="radio"]');
    if (!(radio instanceof HTMLInputElement)) return;
    
    // Reset unavailable state for all options (we'll recalculate below)
    // Only keep unavailable if it's inherently unavailable (data-option-available="false")
    if (radio.dataset.optionAvailable !== 'false') {
      label.classList.remove('unavailable');
      label.removeAttribute('data-unavailable');
      // Reset data-option-available - will be set correctly below
      label.removeAttribute('data-option-available');
      radio.removeAttribute('data-option-available');
    }
  });

  // Check each option value to see if it's available with current selections
  const allRadios = Array.from(picker.querySelectorAll('input[type="radio"]'));
  allRadios.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) return;

    // Check if this option is inherently unavailable
    const isOptionInherentlyUnavailable = radio.dataset.optionAvailable === 'false';

    // Skip if this option is already checked
    if (radio.checked) {
      // Still mark as unavailable if inherently unavailable
      if (isOptionInherentlyUnavailable) {
        label.classList.add('unavailable');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      } else {
        label.classList.remove('unavailable');
        label.removeAttribute('data-unavailable');
        label.setAttribute('data-option-available', 'true');
        radio.setAttribute('data-option-available', 'true');
      }
      return;
    }

    // Get the option name and value for this radio
    const optionName = radio.name.replace(`-${productId}`, '');
    const optionValue = radio.value;

    // If inherently unavailable, mark as unavailable
    if (isOptionInherentlyUnavailable) {
      label.classList.add('unavailable');
      label.setAttribute('data-unavailable', 'true');
      label.setAttribute('data-option-available', 'false');
      radio.setAttribute('data-option-available', 'false');
      return;
    }

    // If no options are selected yet, show all options as available
    // Only check availability when at least one option is selected
    if (Object.keys(selectedOptions).length === 0) {
      // No selections yet - only mark as unavailable if inherently unavailable
      if (radio.dataset.optionAvailable === 'false') {
        label.classList.add('unavailable');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      } else {
        label.classList.remove('unavailable');
        label.removeAttribute('data-unavailable');
        label.setAttribute('data-option-available', 'true');
        radio.setAttribute('data-option-available', 'true');
      }
      return;
    }

    // Create a test selection with this option value
    const testOptions = { ...selectedOptions, [optionName]: optionValue };

    // Find if there's any variant that matches this combination
    // Check both if any variant exists AND if any is available
    let variantExists = false;
    let hasAvailableVariant = false;
    let matchingVariants = [];
    
    for (const variant of variantsList) {
      let matches = true;

      // Check if this variant matches all test options (only check selected + testing option)
      for (const testOptionName in testOptions) {
        const option = optionsList.find((opt) => opt?.name === testOptionName);
        if (!option) {
          matches = false;
          break;
        }

        const optionPosition = option.position - 1;
        const expectedValue = testOptions[testOptionName];
        const variantValue = variant.options?.[optionPosition];

        if (!expectedValue) {
          matches = false;
          break;
        }

        // Normalize values for comparison (handle escaped slashes, case, whitespace)
        const normalizedExpected = expectedValue.toString().toLowerCase().trim().replace(/\\\//g, '/');
        const normalizedVariant = variantValue ? variantValue.toString().toLowerCase().trim().replace(/\\\//g, '/') : '';

        if (!normalizedVariant || normalizedVariant !== normalizedExpected) {
          matches = false;
          break;
        }
      }

      if (matches) {
        variantExists = true;
        matchingVariants.push({ id: variant.id, available: variant.available, options: variant.options });
        if (variant.available) {
          hasAvailableVariant = true;
          // Don't break - continue checking to see all matching variants for debugging
        }
      }
    }

    // Mark as unavailable only if:
    // 1. No variant exists with this combination, OR
    // 2. Variants exist but ALL are unavailable
    const isUnavailable = !variantExists || (variantExists && !hasAvailableVariant);

    // Update unavailable state (matching variant-picker.js pattern)
    if (isUnavailable) {
      label.classList.add('unavailable');
      label.setAttribute('data-unavailable', 'true');
      label.setAttribute('data-option-available', 'false');
      radio.setAttribute('data-option-available', 'false');
    } else {
      label.classList.remove('unavailable');
      label.removeAttribute('data-unavailable');
      label.setAttribute('data-option-available', 'true');
      radio.setAttribute('data-option-available', 'true');
    }
  });
  
  // Verify the updates were applied
  const unavailableCount = picker.querySelectorAll('label[data-unavailable="true"]').length;
  const unavailableSizes = Array.from(picker.querySelectorAll('label[data-unavailable="true"]'))
    .map(label => {
      const radio = label.querySelector('input[type="radio"]');
      return radio instanceof HTMLInputElement ? radio.value : null;
    })
    .filter(Boolean);
}

/**
 * @param {HTMLElement} preview
 * @returns {string}
 */
function extractSwatchBackground(preview) {
  const inlineValue = preview.style.getPropertyValue('--swatch-background')?.trim();
  if (inlineValue) return inlineValue;

  const dataValue = preview.dataset?.swatchBackground?.trim();
  if (dataValue) return dataValue;

  const computedValue = window.getComputedStyle(preview).getPropertyValue('--swatch-background')?.trim();
  return computedValue || '';
}

/**
 * @param {HTMLElement} picker
 * @param {string} optionName
 * @param {string} optionValue
 * @returns {HTMLElement | null}
 */
function findColorSwatchPreview(picker, optionName, optionValue) {
  if (!optionName || optionValue == null) return null;

  const normalizedOptionName = optionName.toLowerCase();
  const normalizedOptionValue = optionValue.toString().toLowerCase().trim();
  const productId = picker.dataset.productId || '';

  const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
  for (const radio of radios) {
    if (!(radio instanceof HTMLInputElement)) continue;
    const radioName = radio.name.replace(`-${productId}`, '').toLowerCase();
    if (radioName !== normalizedOptionName) continue;

    const radioValue = radio.value.toLowerCase().trim();
    if (radioValue !== normalizedOptionValue) continue;

    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) continue;

    const preview = label.querySelector('.color-swatch__preview');
    if (preview instanceof HTMLElement) {
      return preview;
    }
  }

  return null;
}

/**
 * @param {HTMLElement} picker
 * @param {Record<string, string>} selectedOptions
 * @returns {void}
 */
function updateProductCardSwatchBackground(picker, selectedOptions) {
  if (!(picker instanceof HTMLElement)) return;

  const productCard = picker.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  const productFormContainerElement = productCard.querySelector('.product-form-container');
  const productFormContainer = productFormContainerElement instanceof HTMLElement ? productFormContainerElement : null;

  /**
   * @param {HTMLElement | null} element
   * @param {string} value
   */
  const applyBackground = (element, value) => {
    if (!(element instanceof HTMLElement)) return;
    if (value) {
      element.style.setProperty('--selected-swatch-background', value);
      element.dataset.selectedSwatchBackground = value;
    } else {
      element.style.removeProperty('--selected-swatch-background');
      delete element.dataset.selectedSwatchBackground;
    }
  };

  let colorOptionName = '';
  let colorOptionValue = '';

  for (const [optionName, optionValue] of Object.entries(selectedOptions || {})) {
    const lowerName = optionName.toLowerCase();
    if (lowerName === 'color' || lowerName === 'colour') {
      colorOptionName = optionName;
      colorOptionValue = optionValue;
      break;
    }
  }

  if (!colorOptionName || !colorOptionValue) {
    applyBackground(productCard, '');
    applyBackground(productFormContainer, '');
    return;
  }

  const preview = findColorSwatchPreview(picker, colorOptionName, colorOptionValue);
  if (!preview) {
    applyBackground(productCard, '');
    applyBackground(productFormContainer, '');
    return;
  }

  const backgroundValue = extractSwatchBackground(preview);
  applyBackground(productCard, backgroundValue);
  applyBackground(productFormContainer, backgroundValue);
}

/**
 * @param {HTMLElement} picker
 * @param {HTMLElement} productCard
 * @param {HTMLInputElement | null} selectedOption
 * @returns {{ primary: string, secondary: string }}
 */
function getActiveVariantImageUrls(picker, productCard, selectedOption) {
  const urls = {
    primary: '',
    secondary: '',
  };

  if (selectedOption instanceof HTMLInputElement) {
    if (selectedOption.dataset.imageUrl) {
      urls.primary = selectedOption.dataset.imageUrl;
    }
    if (selectedOption.dataset.secondaryImageUrl) {
      urls.secondary = selectedOption.dataset.secondaryImageUrl;
    }
  }

  if (!urls.primary || !urls.secondary) {
    const activeColorInput = picker.querySelector('input[type="radio"][data-image-url][checked]');
    if (activeColorInput instanceof HTMLInputElement) {
      if (!urls.primary && activeColorInput.dataset.imageUrl) {
        urls.primary = activeColorInput.dataset.imageUrl;
      }
      if (!urls.secondary && activeColorInput.dataset.secondaryImageUrl) {
        urls.secondary = activeColorInput.dataset.secondaryImageUrl;
      }
    }
  }

  if (!urls.primary || !urls.secondary) {
    const activeSwiperSwatch = productCard.querySelector('.color-swiper-container .color-swatch[data-active="true"]');
    if (activeSwiperSwatch instanceof HTMLElement) {
      if (!urls.primary && activeSwiperSwatch.dataset.imageUrl) {
        urls.primary = activeSwiperSwatch.dataset.imageUrl;
      }
      if (!urls.secondary && activeSwiperSwatch.dataset.secondaryImageUrl) {
        urls.secondary = activeSwiperSwatch.dataset.secondaryImageUrl;
      }
    }
  }

  const primaryImage = productCard.querySelector('.product-image-primary');
  if (primaryImage instanceof HTMLImageElement) {
    if (!primaryImage.dataset.originalSrc) {
      primaryImage.dataset.originalSrc = primaryImage.currentSrc || primaryImage.src;
    }
    if (!urls.primary) {
      urls.primary = primaryImage.dataset.originalSrc || primaryImage.src;
    }
  }

  const secondaryImage = productCard.querySelector('.product-image-secondary');
  if (secondaryImage instanceof HTMLImageElement) {
    if (!secondaryImage.dataset.originalSrc) {
      secondaryImage.dataset.originalSrc = secondaryImage.currentSrc || secondaryImage.src;
    }
    if (!urls.secondary) {
      urls.secondary = secondaryImage.dataset.originalSrc || secondaryImage.src;
    }
  }

  return urls;
}

/**
 * @param {HTMLElement} productCard
 * @param {string} primaryImageUrl
 * @param {string} secondaryImageUrl
 * @returns {void}
 */
function updateProductCardPrimaryImage(productCard, primaryImageUrl, secondaryImageUrl) {
  if (!(productCard instanceof HTMLElement)) return;

  const primaryImage = productCard.querySelector('.product-image-primary');
  const secondaryImage = productCard.querySelector('.product-image-secondary');

  // Update primary image immediately - no delays
  if (primaryImage instanceof HTMLImageElement && primaryImageUrl) {
    if (!primaryImage.dataset.originalSrc) {
      primaryImage.dataset.originalSrc = primaryImage.currentSrc || primaryImage.src;
    }
    // Set src and srcset immediately for instant update
    primaryImage.src = primaryImageUrl;
    primaryImage.srcset = primaryImageUrl;
  }

  // Update secondary image immediately - no delays
  if (secondaryImage instanceof HTMLImageElement && secondaryImageUrl) {
    if (!secondaryImage.dataset.originalSrc) {
      secondaryImage.dataset.originalSrc = secondaryImage.currentSrc || secondaryImage.src;
    }
    // Set src and srcset immediately for instant update
    secondaryImage.src = secondaryImageUrl;
    secondaryImage.srcset = secondaryImageUrl;
  }
}

async function fetchCartJson() {
  const response = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to load cart details (status: ${response.status})`);
  }

  return response.json();
}

/**
 * @param {any} cart
 * @param {string} [sourceId]
 */
function dispatchCartUpdate(cart, sourceId = 'sunice-product-card') {
  const detail = {
    resource: cart,
    sourceId,
    data: {
      source: sourceId,
      itemCount: cart?.item_count ?? 0,
      sections: undefined
    }
  };

  const event = new CustomEvent('cart:update', {
    detail
  });

  document.dispatchEvent(event);
}

function openCartDrawer() {
  setTimeout(() => {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const cartDrawerEl = /** @type {any} */ (cartDrawer);
      if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
        cartDrawerEl.open();
      } else {
        const cartButton = document.querySelector('.header-actions__action[on\\:click="/open"]');
        if (cartButton instanceof HTMLElement && typeof cartButton.click === 'function') {
          cartButton.click();
        }
      }
    } catch (error) {
      // Failed to open cart drawer
    }
  }, 250);
}

/**
 * @param {HTMLButtonElement | null} button
 * @param {'loading' | 'success' | 'error' | 'idle'} state
 */
function setAddToCartButtonState(button, state) {
  if (!(button instanceof HTMLButtonElement)) return;

  const defaultText = button.dataset.defaultText || button.textContent?.trim() || 'Add to Cart';
  if (!button.dataset.defaultText) {
    button.dataset.defaultText = defaultText;
  }

  if (state === 'loading') {
    button.disabled = true;
    button.classList.add('button--loading');
    button.setAttribute('aria-busy', 'true');
  } else {
    button.disabled = false;
    button.classList.remove('button--loading');
    button.removeAttribute('aria-busy');

    if (state === 'success') {
      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = button.dataset.defaultText || defaultText;
      }, 2000);
    } else if (state === 'error') {
      button.textContent = 'Try Again';
      setTimeout(() => {
        button.textContent = button.dataset.defaultText || defaultText;
      }, 2000);
    } else {
      button.textContent = button.dataset.defaultText || defaultText;
    }
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string | number} productId
 */
async function addProductCardToCart(form, productId) {
  const variantIdInputElement = form.querySelector('input[name="id"]');
  const variantIdInput = variantIdInputElement instanceof HTMLInputElement ? variantIdInputElement : null;
  const quantityInputElement = form.querySelector('input[name="quantity"]');
  const quantityInput = quantityInputElement instanceof HTMLInputElement ? quantityInputElement : null;
  const addButtonElement = form.querySelector('[name="add"]');
  const addButton = addButtonElement instanceof HTMLButtonElement ? addButtonElement : null;

  const variantId = variantIdInput?.value?.trim();
  if (!variantId) {
    alert('Please select all required options before adding to cart.');
    return;
  }

  const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

  setAddToCartButtonState(addButton, 'loading');

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity
          }
        ]
      })
    });

    const data = await response.json();

    if (!response.ok || data?.status) {
      const errorMessage = data?.description || data?.message || 'Unable to add this product to the cart.';
      throw new Error(errorMessage);
    }

    let cart = null;
    try {
      cart = await fetchCartJson();
    } catch (cartError) {
      // Added to cart but failed to refresh cart data
    }

    if (cart) {
      dispatchCartUpdate(cart, `sunice-product-card-${productId}`);
    }

    // If a mobile modal is open, close it after successful add-to-cart
    try {
      /** @type {any} */
      const globalWindow = window;
      if (typeof globalWindow.closeSuniceProductFormModal === 'function') {
        globalWindow.closeSuniceProductFormModal();
      }
    } catch (e) {
      // Failed to close product form modal after add-to-cart
    }

    openCartDrawer();
    setAddToCartButtonState(addButton, 'success');
  } catch (error) {
    alert(error.message || 'There was an issue adding this product to your cart. Please try again.');
    setAddToCartButtonState(addButton, 'error');
  }
}

/**
 * @param {SubmitEvent} event
 * @param {string | number} productId
 */
globalWindow.handleShopifyAddToCart = function(event, productId) {
  event.preventDefault();

  const target = event.target;
  const form =
    target instanceof HTMLFormElement
      ? target
      : event.currentTarget instanceof HTMLFormElement
        ? event.currentTarget
        : null;

  if (!form) {
    return;
  }

  addProductCardToCart(form, productId);
};

// Initialize components when DOM is ready
function initializeSuniceProductCardComponents() {
  // Setup variant pickers
  setupSuniceVariantPicker();

  // Register with theme editor system for automatic re-initialization
  if (typeof globalWindow.registerThemeEditorComponent === 'function') {
    globalWindow.registerThemeEditorComponent(initializeSuniceProductCardComponents, 'sunice-product-cards');
  }

  // Add form validation to ensure complete variant selection
  if (!document.documentElement.hasAttribute('data-sunice-product-card-submit-listener')) {
    document.documentElement.setAttribute('data-sunice-product-card-submit-listener', 'true');
    document.addEventListener('submit', function(event) {
      if (!(event instanceof SubmitEvent)) return;
      const target = event.target;
      if (!(target instanceof HTMLFormElement)) return;
      const form = target;

      const pickerElement = form.querySelector('.sunice-variant-picker');
      if (!(pickerElement instanceof HTMLElement)) return;

      if (form.classList.contains('product-form')) {
        const variantsDataScript = pickerElement.querySelector('.product-variants-data');

        if (variantsDataScript instanceof HTMLScriptElement) {
          let productData;
          try {
            productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
          } catch (error) {
            return;
          }

          const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
          const optionsList = /** @type {Array<any>} */ (productData?.options || []);

          const fieldsets = Array.from(pickerElement.querySelectorAll('fieldset'));

        // Check if all required options are selected
        let allOptionsSelected = true;
          fieldsets.forEach((fieldset) => {
            if (!(fieldset instanceof HTMLFieldSetElement)) return;
            const hasSelection = fieldset.querySelector('input[type="radio"]:checked');
          if (!hasSelection) {
            allOptionsSelected = false;
          }
        });

        if (!allOptionsSelected) {
          event.preventDefault();
          alert('Please select all required options before adding to cart.');
          return false;
        }

        // Validate that the selected combination exists and set the variant ID
          const selectedOptions = /** @type {Record<string, string>} */ ({});
          const radioButtons = Array.from(pickerElement.querySelectorAll('input[type="radio"]:checked'));

          const productId = pickerElement.dataset.productId || '';
          radioButtons.forEach((radio) => {
            if (!(radio instanceof HTMLInputElement)) return;
            const nameWithProductId = radio.name;
          const optionName = nameWithProductId.replace(`-${productId}`, '');
          const optionValue = radio.value;
          selectedOptions[optionName] = optionValue;
        });

        // Find the exact matching variant
          let matchingVariant = null;
          for (const variant of variantsList) {
          let matches = true;
          for (const optionName in selectedOptions) {
              const option = optionsList.find((opt) => opt?.name === optionName);
            if (!option) continue;

            const optionPosition = option.position - 1;
            const expectedValue = selectedOptions[optionName];
              const variantValue = variant.options?.[optionPosition];

            if (variantValue && expectedValue &&
                variantValue.toString().toLowerCase().trim() !== expectedValue.toString().toLowerCase().trim()) {
              matches = false;
              break;
            }
          }
          if (matches) {
            matchingVariant = variant;
            break;
          }
        }

        if (!matchingVariant) {
          event.preventDefault();
          alert('The selected combination is not available. Please choose different options.');
          return false;
        }

        // Set the variant ID in the hidden input
        const variantIdInput = form.querySelector('input[name="id"]');
          if (variantIdInput instanceof HTMLInputElement) {
            variantIdInput.value = String(matchingVariant.id);
          }
      }
    }
    });
  }
}

// Expose initializer globally so other scripts (like recommendations) can re-run it
globalWindow.initializeSuniceProductCardComponents = initializeSuniceProductCardComponents;

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSuniceProductCardComponents);
} else {
  initializeSuniceProductCardComponents();
}

// Also initialize on theme editor events
document.addEventListener('shopify:section:load', initializeSuniceProductCardComponents);
document.addEventListener('shopify:section:reorder', initializeSuniceProductCardComponents);
document.addEventListener('shopify:block:select', initializeSuniceProductCardComponents);
document.addEventListener('shopify:block:deselect', initializeSuniceProductCardComponents);
document.addEventListener('shopify:section:select', initializeSuniceProductCardComponents);
document.addEventListener('shopify:section:deselect', initializeSuniceProductCardComponents);

// Use capture phase for immediate response
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const swatchButton = target.closest('.color-swiper-container .color-swatch');
  if (!(swatchButton instanceof HTMLElement)) return;

  const productCard = swatchButton.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  // Update images IMMEDIATELY - before any other DOM operations
  const imageUrl = swatchButton.dataset.imageUrl || "";
  const secondaryImageUrl = swatchButton.dataset.secondaryImageUrl || "";
  updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);

  // Then update active state in swiper
  const swiperContainer = swatchButton.closest('.color-swiper-container');
  if (swiperContainer instanceof HTMLElement) {
    const activeSwatches = swiperContainer.querySelectorAll('.color-swatch[data-active="true"]');
    activeSwatches.forEach((activeSwatch) => {
      activeSwatch.removeAttribute('data-active');
    });
    swatchButton.setAttribute('data-active', 'true');
  }

  // Find and update the corresponding radio button in the variant picker
  const colorValue = swatchButton.dataset.color;
  if (colorValue) {
    const picker = productCard.querySelector('.sunice-variant-picker');
    if (picker instanceof HTMLElement) {
      // Find the Color or Colour option fieldset
      const colorFieldsets = Array.from(picker.querySelectorAll('fieldset'));
      for (const fieldset of colorFieldsets) {
        if (!(fieldset instanceof HTMLFieldSetElement)) continue;
        const legend = fieldset.querySelector('legend');
        const optionName = legend?.textContent?.trim();
        if (optionName && (optionName.toLowerCase() === 'color' || optionName.toLowerCase() === 'colour')) {
          // Find the radio button with matching value (normalize for comparison)
          const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          let found = false;
          for (const radio of radioButtons) {
            if (!(radio instanceof HTMLInputElement)) continue;
            // Normalize both values for comparison (handle escaped characters, case, whitespace)
            const normalizedRadioValue = radio.value.toLowerCase().trim();
            const normalizedColorValue = colorValue.toLowerCase().trim();
            if (normalizedRadioValue === normalizedColorValue) {
              // Uncheck all other radios in this fieldset first
              radioButtons.forEach((r) => {
                if (r instanceof HTMLInputElement && r !== radio) {
                  r.checked = false;
                }
              });
              // Check the radio button
              radio.checked = true;
              // Call handleVariantChange directly to ensure state updates immediately
              // This will read the checked radios and update unavailable states
              handleVariantChange(radio, picker);
              found = true;
              break;
            }
          }
          break;
        }
      }
    }
  }
}, true); // Use capture phase for faster response

})(); // End IIFE guard

